// components/retailer/RetailerVisualizerRoute.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import RoomVisualizer from "../RoomVisualizer";
import Sidebar from "../Sidebar";
import {
  API_BASE,
  generateMask,
  getDemoRoom,
  getPublicVisualizer,
  toAssetUrl,
  addRoomToCatalog,
  downloadCatalogPdf,
  createSelection,
} from "../../services/api";
import { DEFAULT_REGION_SETTINGS } from "../../data/regionSettings";
import room1 from "../../assets/rooms/room1.jpg";
import room2 from "../../assets/rooms/room2.jpg";
import room4 from "../../assets/rooms/room4.jpg";
import room5 from "../../assets/rooms/room5.jpg";
import room6 from "../../assets/rooms/room6.jpg";
import room7 from "../../assets/rooms/room7.jpg";
import room8 from "../../assets/rooms/room8.jpg";
import room15 from "../../assets/rooms/room15.jpg";
import room16 from "../../assets/rooms/room16.jpg";
import room17 from "../../assets/rooms/room17.jpg";
import ControlBar from "./visualizer/ControlBar";
import VisualizerEntry from "./visualizer/VisualizerEntry";
import { showErrorToast, showSuccessToast, showWarningToast } from "../../services/toast";
import { useCatalogStore } from "../../store/useCatalogStore";


const CM_TO_FT = 1 / 30.48;
const PROCESSING_MESSAGES = [
  "Reading your room layout...",
  "Detecting walls, floors, and ceiling...",
  "Building room geometry...",
  "Preparing your visualizer...",
];

const demoRooms = [
    { roomId: 1, name: "Modern Living Room", image: room1 },
    { roomId: 2, name: "Cozy Bedroom", image: room2 },
    { roomId: 4, name: "Elegant Dining", image: room4 },
    { roomId: 5, name: "Elegant Dining", image: room5 },
    { roomId: 6, name: "Elegant Dining", image: room6 },
    { roomId: 7, name: "Elegant Dining", image: room7 },
    { roomId: 8, name: "Elegant Dining", image: room8 },
    { roomId: 15, name: "Elegant Dining", image: room15 },
    { roomId: 16, name: "Elegant Dining", image: room16 },
    { roomId: 17, name: "Elegant Dining", image: room17 },
];

// function formatDecimal(value, digits = 3) {
//   const number = Number(value);
//   if (!Number.isFinite(number)) return "0";
//   return number.toFixed(digits);
// }

// function formatMoney(value) {
//   const number = Number(value);
//   if (!Number.isFinite(number)) return "$0.00";
//   return `₹${number.toFixed(2)}`;
// }

// function formatRegionName(region) {
//   return typeof region === "string" ? region.replaceAll("_", " ") : "Area";
// }

function getMaterialDimensionSettings(material) {
  if (!["tile", "wallpaper"].includes(material?.type)) return {};

  return {
    ...(typeof material.width_cm === "number"
      ? { tileWidth: material.width_cm * CM_TO_FT }
      : {}),
    ...(typeof material.height_cm === "number"
      ? { tileHeight: material.height_cm * CM_TO_FT }
      : {}),
  };
}

function extractCssUrl(backgroundImage) {
  const match = String(backgroundImage || "").match(/^url\(["']?(.*?)["']?\)$/);
  return match?.[1] || "";
}

function loadExportImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error("Failed to load visualizer background"));
    image.src = src;
  });
}

function normalizeExportAssetUrl(src) {
  if (!src || !API_BASE || !src.startsWith(API_BASE)) return src;

  try {
    const assetUrl = new URL(src);
    const appUrl = new URL(window.location.href);
    const isLocalDev = ["localhost", "127.0.0.1"].includes(appUrl.hostname);
    if (isLocalDev && ["localhost", "127.0.0.1"].includes(assetUrl.hostname)) {
      return `${assetUrl.pathname}${assetUrl.search}`;
    }
  } catch (err) {
    return src;
  }

  return src;
}

function getDownloadFilename(response, fallback) {
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  return match?.[1] || fallback;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function waitForAnimationFrame() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

function buildProductName(product) {
  const parts = [];
  if (product.application_type) {
    parts.push(
      product.application_type.charAt(0).toUpperCase() +
        product.application_type.slice(1),
    );
  }
  parts.push(product.category === "wallpaper" ? "Wallpaper" : "Tile");
  if (product.surface_type) {
    parts.push(
      product.surface_type.charAt(0).toUpperCase() +
        product.surface_type.slice(1),
    );
  }
  const label = parts.join(" · ");
  return product.sku ? `${label} (${product.sku})` : label;
}

function dimensionToMm(value, unit) {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;

  switch (String(unit || "").toLowerCase()) {
    case "mm":
      return number;
    case "cm":
      return number * 10;
    case "inch":
    case "in":
      return number * 25.4;
    case "feet":
    case "ft":
      return number * 304.8;
    default:
      return null;
  }
}

function formatProductSize(size, fallback) {
  if (size?.width && size?.height && size?.unit) {
    return `${size.width}x${size.height} ${size.unit}`;
  }
  return fallback || null;
}

function adaptProduct(product) {
  const firstImage =
    Array.isArray(product.images) && product.images.length > 0
      ? product.images[0].url
      : null;

  const sizeUnit = product.size?.unit || product.size_unit || null;
  const widthMm = dimensionToMm(product.size?.width, sizeUnit);
  const heightMm = dimensionToMm(product.size?.height, sizeUnit);

  return {
    id: product.id,
    name: buildProductName(product),
    sku: product.sku,
    category: product.category,
    product_size: formatProductSize(product.size, product.product_size),
    size: product.size || null,
    size_unit: sizeUnit,
    application_type: product.application_type,
    surface_type: product.surface_type,
    pieces_per_box: product.pieces_per_box,
    price: product.price,
    price_unit: product.price_unit,
    coverage_area_per_box: product.coverage_area_per_box,
    coverage_area_unit: product.coverage_area_unit,
    weight_per_box: product.weight_per_box,
    weight_unit: product.weight_unit,
    thickness_mm: product.thickness_mm,
    image: toAssetUrl(firstImage),
    width_mm: widthMm,
    height_mm: heightMm,
    width_cm: widthMm != null ? widthMm / 10 : null,
    height_cm: heightMm != null ? heightMm / 10 : null,
  };
}

function DemoRoomCard({ image, name, roomId, onDemoSelect }) {
  const handleDemoClick = async () => {
    try {
      const response = await getDemoRoom(roomId);
      if (response.success) {
        onDemoSelect(response);
      } else {
        console.error("Failed to load demo room:", response.error);
      }
    } catch (err) {
      console.error("Failed to load demo room:", err);
    }
  };

  return (
    <button
      onClick={handleDemoClick}
      className="group relative rounded-lg overflow-hidden border border-gray-200 hover:border-accent transition-all hover:shadow-lg bg-white"
    >
      <img
        src={image}
        alt={name}
        className="w-full h-64 md:h-72 xl:h-80 object-cover group-hover:scale-105 transition-transform duration-300"
      />
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4">
        <span className="text-white text-base font-semibold">{name}</span>
      </div>
    </button>
  );
}

export default function RetailerVisualizerRoute() {
  const navigate = useNavigate();
  const { visualizerToken } = useParams();
  const visualizerCaptureRef = useRef(null);

  // catalog
  const [addingToCatalog, setAddingToCatalog] = useState(false);
  const [downloadingCatalog, setDownloadingCatalog] = useState(false);

  // ── Retailer catalog state ──────────────────────────────────
  const [retailer, setRetailer] = useState(null);
  const [tiles, setTiles] = useState([]);
  const [wallpapers, setWallpapers] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState("");

  // ── Room/visualizer state — mirrors VisualizerRoute exactly ──
  const [data, setData] = useState(null);
  const [selectedRegions, setSelectedRegions] = useState({});
  const [regionMaterials, setRegionMaterials] = useState({});
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [draftSettings, setDraftSettings] = useState(DEFAULT_REGION_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [processingMessageIndex, setProcessingMessageIndex] = useState(0);
  const [showInquiryModal, setShowInquiryModal] = useState(false);

  // ── Selection/quotation state ──────────────────────────────────
  const [selectionModal, setSelectionModal] = useState(false);
  const [selectionData, setSelectionData] = useState({
    wastagePercentage: 10,
  });
  const [selectionRows, setSelectionRows] = useState([]);
  const [selectionResult, setSelectionResult] = useState(null);
  const [sendLoading, setSendLoading] = useState(false);
  const [savedQuotation, setSavedQuotation] = useState(null);
  const [lastAppliedMaterial, setLastAppliedMaterial] = useState(null);
  const [exportFormat, setExportFormat] = useState("pdf");
  const [exportLoading, setExportLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const token = localStorage.getItem("retailer_token");

  // selection
  const [isSelectionView, setIsSelectionView] = useState(false);
  const [selectionId, setSelectionId] = useState(null);
  const [selectionLoading, setSelectionLoading] = useState(false);

  const {
  activeCatalogId,
  catalogSessionId,
  addRoomToSession,
  setActiveCatalogId,
  sessionRooms,
  clearCatalogSession
} = useCatalogStore();

  const handleAddRoomToCatalog = async () => {
  const currentRoomName = data?.name || "Living Room";

  const appliedProducts = Object.values(
    regionMaterials || {}
  ).filter((m) => m?.id);

  try {
    setAddingToCatalog(true);

    const renderedImageUrl =
      await captureRenderedVisualizer();

    const payload = {
      catalog_id: activeCatalogId,
      session_id: catalogSessionId,
      room_name: currentRoomName,
      room_image_url: renderedImageUrl,
      products: appliedProducts.map((prod, index) => ({
        product_id: prod.id,
        position: index,
      })),
    };

    const result = await addRoomToCatalog(payload);

    const responseData =
      result?.data || result;

    const newCatalogId =
      responseData?.catalog_id ||
      responseData?.id;

    if (newCatalogId) {
      setActiveCatalogId(newCatalogId);
    }

    addRoomToSession({
      id: responseData?.room_id,
      catalog_id: responseData?.catalog_id,
      session_id: responseData?.session_id,
      room_name: responseData?.room_name,
      room_image_url: responseData?.room_image_url,
      products: payload.products,
    });

    showSuccessToast(
      `Room added to catalog! Total rooms: ${
        sessionRooms.length + 1
      }`
    );
  } catch (err) {
    console.error(
      "Error adding room to catalog:",
      err
    );

    showErrorToast(
      "Failed to add room to catalog!"
    );
  } finally {
    setAddingToCatalog(false);
  }
};

  // 2. Download Catalog PDF
  const handleDownloadCatalog = async () => {
  if (
    !activeCatalogId ||
    !catalogSessionId
  ) {
    alert(
      "No active catalog session found."
    );
    return;
  }

  try {
    setDownloadingCatalog(true);

    await downloadCatalogPdf(
      activeCatalogId,
      catalogSessionId
    );
  } catch (err) {
    console.error(
      "Catalog download error:",
      err
    );

    alert(
      err.message ||
      "Failed to download catalog"
    );
  } finally {
    setDownloadingCatalog(false);
  }
};

  const selectedRegion = Object.keys(selectedRegions).find(
    (region) => selectedRegions[region],
  );

  const appliedQuotationItems = useMemo(
    () =>
      Object.entries(regionMaterials || {})
        .filter(([, material]) => material?.id)
        .map(([region, material]) => ({ region, material })),
    [regionMaterials],
  );

  const uniqueAppliedProductCount = useMemo(
    () => new Set(appliedQuotationItems.map((item) => item.material.id)).size,
    [appliedQuotationItems],
  );

  const quotationRows =
    selectionRows.length > 0
      ? selectionRows
      : appliedQuotationItems.map((item) => ({
          region: item.region,
          material: item.material,
          areaSqft: "",
          areaSqm: "",
          boxes: "",
        }));

  const previewTotals = useMemo(
    () =>
      quotationRows.reduce(
        (totals, row) => ({
          sqft: totals.sqft + Number(row.areaSqft || 0),
          sqm: totals.sqm + Number(row.areaSqm || 0),
          boxes: totals.boxes + Number(row.boxes || 0),
        }),
        { sqft: 0, sqm: 0, boxes: 0 },
      ),
    [quotationRows],
  );

  // ── Fetch retailer catalog once on mount ────────────────────
  useEffect(() => {
    let cancelled = false;

    const fetchCatalog = async () => {
      setCatalogLoading(true);
      setCatalogError("");
      try {
        const res = await getPublicVisualizer(visualizerToken);
        if (cancelled) return;

        const payload = res?.data || res; // tolerate either { data: {...} } or flat shape
        setRetailer(payload?.retailer || null);
        setTiles((payload?.tiles || []).map(adaptProduct));
        setWallpapers((payload?.wallpapers || []).map(adaptProduct));
        if (payload?.retailer?.catalog_id || payload?.catalog_id) {
          setActiveCatalogId(
            payload?.retailer?.catalog_id || payload?.catalog_id,
          );
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setCatalogError(
            "This visualizer link is invalid or no longer active.",
          );
        }
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    };

    if (visualizerToken) fetchCatalog();
    return () => {
      cancelled = true;
    };
  }, [visualizerToken]);

  useEffect(() => {
    setRegionMaterials({});
    setSelectedRegions({});
  }, [data?.project_id, data?.image_path]);

  useEffect(() => {
    if (!loading) {
      setProcessingMessageIndex(0);
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setProcessingMessageIndex(
        (index) => (index + 1) % PROCESSING_MESSAGES.length,
      );
    }, 1800);

    return () => window.clearInterval(intervalId);
  }, [loading]);

  useEffect(() => {
    if (!selectedRegion) {
      setSelectedMaterial(null);
      setDraftSettings(DEFAULT_REGION_SETTINGS);
      return;
    }

    const applied = regionMaterials[selectedRegion];
    if (applied) {
      setSelectedMaterial({
        ...applied,
        id: applied.id,
        type: applied.type,
        value: applied.value,
        width_cm: applied.width_cm,
        height_cm: applied.height_cm,
      });
      setDraftSettings(applied.settings ?? DEFAULT_REGION_SETTINGS);
      return;
    }

    setSelectedMaterial(null);
    setDraftSettings(DEFAULT_REGION_SETTINGS);
  }, [selectedRegion, regionMaterials]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    try {
      const response = await generateMask(file);
      setData(response);
    } catch (err) {
      console.error(err);
      alert("Failed to process room image");
    } finally {
      setLoading(false);
      event.target.value = "";
    }
  };

  const handleDemoSelect = async (demoData) => {
    setLoading(true);
    try {
      setData(demoData);
    } catch (err) {
      console.error(err);
      alert("Failed to load demo room");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMaterial = (material) => {
    setSelectedMaterial(material);

    const materialDimensionSettings = getMaterialDimensionSettings(material);
    if (Object.keys(materialDimensionSettings).length > 0) {
      setDraftSettings((prev) => ({
        ...prev,
        ...materialDimensionSettings,
      }));
    }
  };

  const handleDirectApply = (material) => {
    if (!selectedRegion) return;

    const settingsToSave = { ...draftSettings };
    Object.assign(settingsToSave, getMaterialDimensionSettings(material));

    const materialToSave = {
      ...material,
      settings: settingsToSave,
    };

    setRegionMaterials((prev) => ({
      ...prev,
      [selectedRegion]: materialToSave,
    }));

    setLastAppliedMaterial(materialToSave);
  };

  const handleGroutChange = (key, value) => {
    if (!selectedRegion || !regionMaterials[selectedRegion]) return;

    setRegionMaterials((prev) => ({
      ...prev,
      [selectedRegion]: {
        ...prev[selectedRegion],
        settings: {
          ...prev[selectedRegion].settings,
          [key]: value,
        },
      },
    }));
  };

  const handleRemoveGrout = () => {
    if (!selectedRegion || !regionMaterials[selectedRegion]) return;

    setRegionMaterials((prev) => ({
      ...prev,
      [selectedRegion]: {
        ...prev[selectedRegion],
        settings: {
          ...prev[selectedRegion].settings,
          groutWidth: 0,
          groutColor: DEFAULT_REGION_SETTINGS.groutColor,
        },
      },
    }));
    setDraftSettings((prev) => ({
      ...prev,
      groutWidth: 0,
      groutColor: DEFAULT_REGION_SETTINGS.groutColor,
    }));
  };

  const applyMaterial = () => {
    if (!selectedRegion || !selectedMaterial) return;

    const settingsToSave = { ...draftSettings };
    Object.assign(
      settingsToSave,
      getMaterialDimensionSettings(selectedMaterial),
    );

    const materialToSave = {
      ...selectedMaterial,
      settings: settingsToSave,
    };

    setRegionMaterials((prev) => ({
      ...prev,
      [selectedRegion]: materialToSave,
    }));

    setLastAppliedMaterial(materialToSave);
  };

  const handleClearMaterials = () => {
    setRegionMaterials({});
    setSelectedRegions({});
    setSelectedMaterial(null);
    setDraftSettings(DEFAULT_REGION_SETTINGS);
  };

  const handleAddToSelection = async () => {
  if (appliedQuotationItems.length === 0) {
    showWarningToast("Please select and apply a material first.");
    return;
  }

  try {
    setSelectionLoading(true);

    // Retrieve visualizer token from state, props, or URL
    const token =
      visualizerToken ||
      new URLSearchParams(window.location.search).get("token");

    if (!token) {
      alert("Visualizer token is missing.");
      return;
    }

    // 1. Map items to match `SelectionItemCreate` backend schema
    const formattedItems = appliedQuotationItems.map((item) => ({
      region: item.region,
      product_id: item.material.id,
      product_sku: item.material.sku || null,
      product_name: item.material.name || null,
      product_size: item.material.size?.name || null,
      application_type: item.material.application_type || item.material.material || null,
      pieces_per_box: item.material.pieces_per_box ? Number(item.material.pieces_per_box) : null,
      coverage_area_per_box: item.material.coverage_area_per_box
        ? String(item.material.coverage_area_per_box)
        : null,
      input_type: item.material.input_type || "sq_ft",
      input_value: item.material.input_value ? Number(item.material.input_value) : 0,
      area_sqft: item.material.area_sqft ? Number(item.material.area_sqft) : 0,
      area_sqm: item.material.area_sqm ? Number(item.material.area_sqm) : 0,
      boxes_needed: item.material.boxes_needed ? Number(item.material.boxes_needed) : 0,
      price_per_box: item.material.price_per_box ? Number(item.material.price_per_box) : null,
      price_per_sqm: item.material.price_per_sqm ? Number(item.material.price_per_sqm) : null,
      total_price: item.material.total_price ? Number(item.material.total_price) : 0,
      price_unit: item.material.price_unit || "Box",
      thickness: item.material.thickness ? Number(item.material.thickness) : null,
      weight_kg: item.material.weight_kg ? Number(item.material.weight_kg) : null,
      image_url: item.material.images && item.material.images.length > 0 ? item.material.images[0].url : null,
    }));

    // 2. Compute aggregate totals for `SelectionTotals` backend schema
    const totals = formattedItems.reduce(
      (acc, item) => ({
        area_sqft: acc.area_sqft + (item.area_sqft || 0),
        area_sqm: acc.area_sqm + (item.area_sqm || 0),
        boxes_needed: acc.boxes_needed + (item.boxes_needed || 0),
        total_price: acc.total_price + (item.total_price || 0),
      }),
      { area_sqft: 0, area_sqm: 0, boxes_needed: 0, total_price: 0 }
    );

    // 3. Final payload matching `SelectionCreate`
    const payload = {
      visualizer_token: token,
      wastage_percentage: 10,
      items: formattedItems,
      totals: totals,
    };

    const result = await createSelection(payload);

    if (result?.selection_id) {
      setSelectionId(result.selection_id);
      navigate(`/selection/${result.selection_id}`);
    } else {
      setIsSelectionView(true);
    }
  } catch (error) {
    console.error("Selection error:", error);
    alert(error.message || "Error saving selection. Please try again.");
  } finally {
    setSelectionLoading(false);
  }
};

  // const SQM_TO_SQFT = 10.7639;
  // const SQFT_TO_SQM = 0.092903;

  // const updateSelectionRow = (index, field, value) => {
  //   setSelectionResult(null);
  //   setSelectionRows((prev) =>
  //     prev.map((row, rowIndex) => {
  //       if (rowIndex !== index) return row;

  //       if (field === "areaSqft") {
  //         const sqft = Number(value);
  //         return {
  //           ...row,
  //           areaSqft: value,
  //           areaSqm:
  //             Number.isFinite(sqft) && sqft > 0
  //               ? formatDecimal(sqft * SQFT_TO_SQM, 3)
  //               : "",
  //           boxes: "",
  //         };
  //       }

  //       if (field === "areaSqm") {
  //         const sqm = Number(value);
  //         return {
  //           ...row,
  //           areaSqm: value,
  //           areaSqft:
  //             Number.isFinite(sqm) && sqm > 0
  //               ? formatDecimal(sqm * SQM_TO_SQFT, 3)
  //               : "",
  //           boxes: "",
  //         };
  //       }

  //       return {
  //         ...row,
  //         boxes: value,
  //         areaSqft: "",
  //         areaSqm: "",
  //       };
  //     }),
  //   );
  // };

  // const getRowCalculationInput = (row) => {
  //   if (Number(row.boxes) > 0) {
  //     return { inputType: "box", inputValue: row.boxes };
  //   }
  //   if (Number(row.areaSqft) > 0) {
  //     return { inputType: "sq_ft", inputValue: row.areaSqft };
  //   }
  //   if (Number(row.areaSqm) > 0) {
  //     return { inputType: "sq_mt", inputValue: row.areaSqm };
  //   }
  //   return null;
  // };

  // const handleSelectionCalculate = async () => {
  //   if (quotationRows.length === 0) {
  //     alert("Please select and apply a material first");
  //     return;
  //   }

  //   const rowInputs = quotationRows.map(getRowCalculationInput);
  //   if (rowInputs.some((input) => !input)) {
  //     alert("Please enter sq ft, sq mt, or boxes for each selected product");
  //     return;
  //   }

  //   setSelectionLoading(true);
  //   try {
  //     const responses = await Promise.all(
  //       quotationRows.map((row, index) =>
  //         calculateSelection(
  //           visualizerToken,
  //           row.material.id,
  //           rowInputs[index].inputType,
  //           rowInputs[index].inputValue,
  //           selectionData.wastagePercentage,
  //         ),
  //       ),
  //     );

  //     const items = responses.map((response, index) => ({
  //       region: quotationRows[index].region,
  //       material: quotationRows[index].material,
  //       input: rowInputs[index],
  //       result: response.data,
  //     }));

  //     setSelectionResult({
  //       items,
  //       totals: {
  //         area_sqft: items.reduce(
  //           (sum, item) => sum + Number(item.result?.area_sqft || 0),
  //           0,
  //         ),
  //         area_sqm: items.reduce(
  //           (sum, item) => sum + Number(item.result?.area_sqm || 0),
  //           0,
  //         ),
  //         boxes_needed: items.reduce(
  //           (sum, item) => sum + Number(item.result?.boxes_needed || 0),
  //           0,
  //         ),
  //         total_price: items.reduce(
  //           (sum, item) => sum + Number(item.result?.total_price || 0),
  //           0,
  //         ),
  //       },
  //     });
  //   } catch (err) {
  //     console.error(err);
  //     alert("Failed to calculate selection");
  //   } finally {
  //     setSelectionLoading(false);
  //   }
  // };

  const brandLogoUrl = useMemo(
    () => toAssetUrl(retailer?.logo_url),
    [retailer],
  );

  const brandName = retailer?.company_name;

  // const closeQuotationPage = () => {
  //   setSelectionModal(false);
  //   setSelectionResult(null);
  //   setSavedQuotation(null);
  // };

  // const handleSendToRetailer = async () => {
  //   if (!selectionResult?.items?.length) {
  //     alert("Please calculate the quotation first");
  //     return;
  //   }

  //   setSendLoading(true);
  //   try {
  //     const items = selectionResult.items.map((item) => ({
  //       region: item.region,
  //       product_id: item.material.id,
  //       product_sku: item.material.sku || null,
  //       product_name: item.material.name || null,
  //       product_size: item.material.product_size || null,
  //       application_type: item.material.application_type || null,
  //       pieces_per_box: item.material.pieces_per_box || null,
  //       coverage_area_per_box:
  //         item.result?.coverage_area_per_box != null
  //           ? String(item.result.coverage_area_per_box)
  //           : item.material.coverage_area_per_box != null
  //             ? String(item.material.coverage_area_per_box)
  //             : null,
  //       input_type: item.input.inputType,
  //       input_value: Number(item.input.inputValue),
  //       area_sqft:
  //         item.result?.area_sqft != null ? Number(item.result.area_sqft) : null,
  //       area_sqm:
  //         item.result?.area_sqm != null ? Number(item.result.area_sqm) : null,
  //       boxes_needed:
  //         item.result?.boxes_needed != null
  //           ? Number(item.result.boxes_needed)
  //           : null,
  //       price_per_box:
  //         item.result?.price_per_box != null
  //           ? Number(item.result.price_per_box)
  //           : null,
  //       price_per_sqm:
  //         item.result?.price_per_sqm != null
  //           ? Number(item.result.price_per_sqm)
  //           : null,
  //       total_price:
  //         item.result?.total_price != null
  //           ? Number(item.result.total_price)
  //           : null,
  //       price_unit: item.result?.price_unit || item.material.price_unit || null,
  //     }));

  //     const response = await sendQuotation(
  //       visualizerToken,
  //       selectionData.wastagePercentage,
  //       items,
  //       selectionResult.totals,
  //     );

  //     setSavedQuotation(response.data || response);
  //   } catch (err) {
  //     console.error(err);
  //     alert("Failed to send quotation to retailer");
  //   } finally {
  //     setSendLoading(false);
  //   }
  // };

  // const handleDownloadPdf = () => {
  //   if (!savedQuotation) return;

  //   const printWindow = window.open("", "_blank", "width=900,height=700");
  //   if (!printWindow) {
  //     alert("Please allow pop-ups to download the PDF");
  //     return;
  //   }

  //   const rowsHtml = (savedQuotation.items || [])
  //     .map(
  //       (item, index) => `
  //     <tr>
  //       <td>${index + 1}</td>
  //       <td>${item.region || "-"}</td>
  //       <td>${item.product_sku || item.product_name || "-"}</td>
  //       <td>${item.product_size || "-"}</td>
  //       <td>${item.application_type || "-"}</td>
  //       <td>${item.pieces_per_box || "-"}</td>
  //       <td>${item.coverage_area_per_box || "-"}</td>
  //       <td>${item.price_per_box || item.price_per_sqm || "-"}</td>
  //       <td>${item.area_sqft || "-"} / ${item.area_sqm || "-"}</td>
  //       <td>${item.boxes_needed || "-"}</td>
  //       <td>${item.total_price || "0"}</td>
  //     </tr>
  //   `,
  //     )
  //     .join("");

  //   printWindow.document.write(`
  //     <html>
  //       <head>
  //         <title>Quotation ${savedQuotation.id || ""}</title>
  //         <style>
  //           body { font-family: Arial, sans-serif; color: #111; padding: 24px; }
  //           h1 { margin: 0 0 8px; font-size: 22px; }
  //           p { margin: 4px 0; color: #444; }
  //           table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 12px; }
  //           th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
  //           th { background: #f5f5f5; }
  //           .totals { margin-top: 20px; width: 320px; margin-left: auto; }
  //           .totals div { display: flex; justify-content: space-between; padding: 4px 0; }
  //           .total-price { font-size: 18px; font-weight: bold; color: #dc2626; }
  //         </style>
  //       </head>
  //       <body>
  //         <h1>${brandName || "Quotation"}</h1>
  //         <p>Quotation ID: ${savedQuotation.id || "-"}</p>
  //         <p>Status: Sent to Retailer</p>
  //         <p>Wastage: ${savedQuotation.wastage_percentage || 0}%</p>
  //         <table>
  //           <thead>
  //             <tr>
  //               <th>Sr.</th>
  //               <th>Area</th>
  //               <th>Product</th>
  //               <th>Size</th>
  //               <th>Application</th>
  //               <th>Pieces/Box</th>
  //               <th>Area/Box</th>
  //               <th>Price</th>
  //               <th>Required Area (sqft / sqmt)</th>
  //               <th>Boxes</th>
  //               <th>Total</th>
  //             </tr>
  //           </thead>
  //           <tbody>${rowsHtml}</tbody>
  //         </table>
  //         <div class="totals">
  //           <div><span>Area (sq ft)</span><strong>${formatDecimal(savedQuotation.total_area_sqft)}</strong></div>
  //           <div><span>Area (sq mt)</span><strong>${formatDecimal(savedQuotation.total_area_sqm)}</strong></div>
  //           <div><span>Boxes Needed</span><strong>${formatDecimal(savedQuotation.total_boxes, 0)}</strong></div>
  //           <div class="total-price"><span>Total Price</span><span>${formatMoney(savedQuotation.total_price)}</span></div>
  //         </div>
  //       </body>
  //     </html>
  //   `);
  //   printWindow.document.close();
  //   printWindow.focus();
  //   printWindow.print();
  // };

  const captureRenderedVisualizer = async () => {
    const container = visualizerCaptureRef.current;
    const webglCanvas = container?.querySelector("canvas");
    const visualizerFrame = webglCanvas?.closest(".relative");

    if (!container || !webglCanvas || !visualizerFrame) {
      throw new Error("Visualizer is not ready to export");
    }

    await waitForAnimationFrame();

    const backgroundUrl = normalizeExportAssetUrl(
      extractCssUrl(visualizerFrame.style.backgroundImage),
    );
    if (!backgroundUrl) {
      throw new Error("Visualizer background is not ready to export");
    }

    const outputWidth =
      data?.camera_intrinsics?.width ||
      webglCanvas.width ||
      visualizerFrame.clientWidth;
    const outputHeight =
      data?.camera_intrinsics?.height ||
      webglCanvas.height ||
      visualizerFrame.clientHeight;
    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = outputWidth;
    exportCanvas.height = outputHeight;

    const context = exportCanvas.getContext("2d");
    const backgroundImage = await loadExportImage(backgroundUrl);
    context.drawImage(backgroundImage, 0, 0, outputWidth, outputHeight);
    context.drawImage(webglCanvas, 0, 0, outputWidth, outputHeight);

    return exportCanvas.toDataURL("image/png");
  };

  const buildExportProducts = () => {
    if (selectionResult?.items?.length) {
      return selectionResult.items.map((item) => ({
        product_id: item.material.id,
        region: item.region,
        product_name: item.material.name || null,
        product_sku: item.material.sku || null,
        product_size: item.material.product_size || null,
        application_type: item.material.application_type || null,
        input_type: item.input.inputType,
        input_value: Number(item.input.inputValue),
        area_sqft:
          item.result?.area_sqft != null ? Number(item.result.area_sqft) : null,
        area_sqm:
          item.result?.area_sqm != null ? Number(item.result.area_sqm) : null,
        boxes_needed:
          item.result?.boxes_needed != null
            ? Number(item.result.boxes_needed)
            : null,
        total_price:
          item.result?.total_price != null
            ? Number(item.result.total_price)
            : null,
        price_unit: item.result?.price_unit || item.material.price_unit || null,
      }));
    }

    return appliedQuotationItems.map((item) => ({
      product_id: item.material.id,
      region: item.region,
      product_name: item.material.name || null,
      product_sku: item.material.sku || null,
      product_size: item.material.product_size || null,
      application_type: item.material.application_type || null,
      price_unit: item.material.price_unit || null,
    }));
  };

  const handleExportDesign = async () => {
    setExportLoading(true);
    try {
      const renderedImage = await captureRenderedVisualizer();
      const response = await fetch(`${API_BASE}/api/visualizer/export`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          visualizer_token: visualizerToken,
          export_format: exportFormat,
          rendered_image: renderedImage,
          title: `${brandName || "Room"} Design`,
          products: buildExportProducts(),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to export visualizer design");
      }

      const blob = await response.blob();
      const filename = getDownloadFilename(
        response,
        `visualizer-design.${exportFormat}`,
      );
      downloadBlob(blob, filename);
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to export visualizer design");
    } finally {
      setExportLoading(false);
    }
  };

  // ── Loading / error states for the catalog fetch ────────────
  if (catalogLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500 text-sm">Loading visualizer…</div>
      </div>
    );
  }

  if (catalogError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Visualizer Unavailable
          </h1>
          <p className="text-gray-600 text-sm">{catalogError}</p>
        </div>
      </div>
    );
  }

  // ── Room already uploaded/selected → show 3D visualizer ─────
  if (data) {
    return (
      <div className="w-screen h-screen bg-white flex overflow-hidden">
        <Sidebar
          onSelect={handleSelectMaterial}
          settings={draftSettings}
          setSettings={setDraftSettings}
          onApply={applyMaterial}
          selectedRegion={selectedRegion}
          selectedMaterial={selectedMaterial}
          regionMaterials={regionMaterials}
          tiles={tiles}
          wallpapers={wallpapers}
          brandLogoUrl={brandLogoUrl}
          brandName={brandName}
          visualizerToken={visualizerToken}
          onClearMaterials={handleClearMaterials}
          onDirectApply={handleDirectApply}
          onGroutChange={handleGroutChange}
          onRemoveGrout={handleRemoveGrout}
        />

        <div className="flex-1 min-w-0 h-full relative flex items-center justify-center overflow-hidden">
          {/* Navbar inside visualizer section */}
          <div className="absolute top-4 left-6 right-6 z-50 flex items-center justify-between pointer-events-none">
            {/* Left Action Controls */}
            <div className="flex items-center gap-2 pointer-events-auto">
              <button
                onClick={() => setData(null)}
                className="p-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg shadow-sm hover:bg-gray-50 transition-colors flex items-center justify-center"
                title="Back"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
            </div>

            {/* Top Floating Control Bar */}
            <ControlBar
              handleAddToSelection={handleAddToSelection}
              exportFormat={exportFormat}
              setExportFormat={setExportFormat}
              exportLoading={exportLoading}
              handleExportDesign={handleExportDesign}
              setData={setData}
              isMenuOpen={isMenuOpen}
              setIsMenuOpen={setIsMenuOpen}
              handleAddRoomToCatalog={handleAddRoomToCatalog}
              addingToCatalog={addingToCatalog}
              handleDownloadCatalog={handleDownloadCatalog}
              downloadingCatalog={downloadingCatalog}
              selectionLoading={selectionLoading}
              sessionRooms={sessionRooms}
              clearCatalogSession={clearCatalogSession}
            />
          </div>

          <div
            ref={visualizerCaptureRef}
            className="w-full h-full min-w-0 overflow-hidden flex items-center justify-center"
          >
            <RoomVisualizer
              key={data.project_id || data.image_path || "room-visualizer"}
              data={data}
              selectedRegions={selectedRegions}
              setSelectedRegions={setSelectedRegions}
              regionMaterials={regionMaterials}
            />
          </div>
        </div>
      </div>
    );
  }

  // ── No room yet → upload / demo room screen, retailer-branded ─
  return (
    <VisualizerEntry
      brandLogoUrl={brandLogoUrl}
      brandName={brandName}
      handleUpload={handleUpload}
      handleDemoSelect={handleDemoSelect}
      demoRooms={demoRooms}
      room4={room4}
      loading={loading}
      PROCESSING_MESSAGES={PROCESSING_MESSAGES}
      processingMessageIndex={processingMessageIndex}
      DemoRoomCard={DemoRoomCard}
    />
  );
}
